import { useCallback, useMemo, useState } from 'react';
import { useUserSettings } from 'selectors/user';
import { isFinite } from 'lodash';
import { convertToSeconds, oneDaySeconds } from 'utils/time';
import { doUpdateUserSettings } from 'actions/user';
import type { UserSettings } from 'types/user';
import { useAppDispatch } from 'stores/hooks';

const DAYS_TO_SNOOZE = 30;
const PROMPT_TO_SNOOZE_AFTER_DAYS = 3;

export type UpdateUserFeedbackActionTypes = 'SNOOZE_CONFIRM' | 'SNOOZE_CANCEL' | 'HIDE' | 'SUBMIT';

const useForecastFeedbackUserSettings = ({
  isAnonymous,
  promptFrequency = 2,
}: {
  isAnonymous: boolean;
  promptFrequency?: number;
}) => {
  const currentTimestamp = convertToSeconds(new Date().getTime()); // unix timestamp (UTC-0)
  const dispatch = useAppDispatch();
  const userSettings = useUserSettings();

  // used to track session status when it should close for the session
  // after submitting or closing with the 'x'
  const [isForcedClosed, setIsForcedClosed] = useState(false);

  const isForecastFeedbackSnoozed = useMemo(() => {
    if (
      userSettings?.forecastFeedback?.ctaSnoozeUntilTimestamp &&
      isFinite(userSettings.forecastFeedback.ctaSnoozeUntilTimestamp) &&
      userSettings.forecastFeedback.ctaSnoozeUntilTimestamp > currentTimestamp
    ) {
      return true;
    }
    return false;
  }, [currentTimestamp, userSettings?.forecastFeedback?.ctaSnoozeUntilTimestamp]);

  const isSnoozePromptVisible = useMemo(() => {
    if (
      userSettings?.forecastFeedback?.ctaHiddenCount &&
      userSettings.forecastFeedback.ctaHiddenCount >= PROMPT_TO_SNOOZE_AFTER_DAYS &&
      !isForecastFeedbackSnoozed &&
      isForcedClosed
    ) {
      return true;
    }
    return false;
  }, [isForcedClosed, isForecastFeedbackSnoozed, userSettings?.forecastFeedback?.ctaHiddenCount]);

  const isPromptVisible = useMemo(() => {
    if (isAnonymous || isForcedClosed) {
      return false;
    }

    // Check if we're snoozing
    if (isForecastFeedbackSnoozed) {
      return false;
    }

    // check if the hide the prompt within the last X days (based on prompt frequency which can be controlled by split)
    if (
      userSettings?.forecastFeedback?.ctaHiddenTimestamp &&
      isFinite(userSettings.forecastFeedback.ctaHiddenTimestamp)
    ) {
      const daysDiff =
        (currentTimestamp - userSettings.forecastFeedback.ctaHiddenTimestamp) / oneDaySeconds;
      if (daysDiff < promptFrequency) {
        return false;
      }
    }

    return true;
  }, [
    currentTimestamp,
    isAnonymous,
    isForcedClosed,
    isForecastFeedbackSnoozed,
    promptFrequency,
    userSettings?.forecastFeedback?.ctaHiddenTimestamp,
  ]);

  const updateForecastFeedbackUserSetting = useCallback(
    (action: UpdateUserFeedbackActionTypes) => {
      const settingsToUpdate: Partial<UserSettings['forecastFeedback']> = {};
      switch (action) {
        case 'SNOOZE_CONFIRM':
          settingsToUpdate.ctaSnoozeUntilTimestamp =
            convertToSeconds(new Date().getTime()) + DAYS_TO_SNOOZE * oneDaySeconds;
          break;
        case 'SNOOZE_CANCEL':
          settingsToUpdate.ctaHiddenTimestamp = convertToSeconds(new Date().getTime());
          settingsToUpdate.ctaHiddenCount = 0;
          break;
        case 'HIDE':
          setIsForcedClosed(true);
          settingsToUpdate.ctaHiddenTimestamp = convertToSeconds(new Date().getTime());

          if (
            !isForecastFeedbackSnoozed &&
            isFinite(userSettings?.forecastFeedback?.ctaSnoozeUntilTimestamp)
          ) {
            settingsToUpdate.ctaSnoozeUntilTimestamp = null;

            // reset hidden count if hiding after snoozing
            settingsToUpdate.ctaHiddenCount = 1;
          } else {
            settingsToUpdate.ctaHiddenCount =
              (userSettings?.forecastFeedback?.ctaHiddenCount || 0) + 1;
          }
          break;
        case 'SUBMIT':
          setIsForcedClosed(true);
          settingsToUpdate.ctaHiddenTimestamp = convertToSeconds(new Date().getTime());
          settingsToUpdate.ctaHiddenCount = 0;
          if (!isForecastFeedbackSnoozed) {
            settingsToUpdate.ctaSnoozeUntilTimestamp = null;
          }
          break;
        default:
          break;
      }
      dispatch(
        doUpdateUserSettings({
          forecastFeedback: {
            ...settingsToUpdate,
          },
        }),
      );
    },
    [
      dispatch,
      isForecastFeedbackSnoozed,
      userSettings?.forecastFeedback?.ctaHiddenCount,
      userSettings?.forecastFeedback?.ctaSnoozeUntilTimestamp,
    ],
  );

  return {
    isPromptVisible,
    isSnoozePromptVisible,
    updateForecastFeedbackUserSetting,
  };
};

export default useForecastFeedbackUserSettings;
